import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import AppMenu from '../components/AppMenu';
import AuctionFinishedPanel from '../components/AuctionFinishedPanel';
import AuctionLog from '../components/AuctionLog';
import AuctionResultPanel from '../components/AuctionResultPanel';
import AuctionSettingsModal from '../components/AuctionSettingsModal';
import AuctioneerLobby from '../components/AuctioneerLobby';
import CallModeSwitch from '../components/CallModeSwitch';
import CloseLobbyModal from '../components/CloseLobbyModal';
import ConnectionBanner from '../components/ConnectionBanner';
import LotCard from '../components/LotCard';
import NominatePlayerModal from '../components/NominatePlayerModal';
import PendingCallPanel from '../components/PendingCallPanel';
import PhaseTrack from '../components/PhaseTrack';
import PlayerBrowser from '../components/PlayerBrowser';
import PlayerDisplay from '../components/PlayerDisplay';
import PlayerSearch from '../components/PlayerSearch';
import ReleasePlayerModal from '../components/ReleasePlayerModal';
import SplitPane from '../components/SplitPane';
import TeamAssignment from '../components/TeamAssignment';
import TeamColumn from '../components/TeamColumn';
import Toasts from '../components/Toasts';
import {
    BASE_HOLDERS,
    CALL_MODES,
    CLOSE_CODES,
    LOBBY_CLOSED_MESSAGE,
    MODES,
    STATES,
} from '../domain/constants';
import { assignedPlayerIds } from '../domain/team';
import useAuctionSocket from '../hooks/useAuctionSocket';
import { deleteAuction, fetchPlayers, indexPlayers } from '../lib/api';
import { clearSession, loadSession } from '../lib/session';
import { downloadCsv, encodeTeams, parseTeams } from '../lib/teamsCsv';

// Una sola pagina per l'asta live e per quella di riparazione, e — dal
// restyle — un solo albero di componenti per telefono e desktop.
//
// Prima le due forme erano alberi separati scelti da una media query
// (`.desktop-only`), e sotto i 768px meta applicazione spariva: un
// partecipante col telefono non poteva chiamare un giocatore quando toccava a
// lui, e il banditore non poteva aprire le impostazioni ne importare le rose.
// Adesso i componenti sono gli stessi a ogni larghezza e cambiano forma da
// soli, in base alla larghezza del proprio contenitore.

export default function OnlineAuctionPage() {
    const { lobbyName } = useParams();
    const navigate = useNavigate();

    const session = useMemo(() => loadSession(lobbyName), [lobbyName]);
    const [catalogue, setCatalogue] = useState({});
    const [players, setPlayers] = useState([]);
    const [quotazioniAvailable, setQuotazioniAvailable] = useState(true);
    const [catalogueError, setCatalogueError] = useState(null);

    const [showSettings, setShowSettings] = useState(false);
    const [showAssignTeams, setShowAssignTeams] = useState(false);
    const [releaseTarget, setReleaseTarget] = useState(null);
    const [showCloseLobby, setShowCloseLobby] = useState(false);
    const [closing, setClosing] = useState({ pending: false, error: null });
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    // Il giocatore toccato nella ricerca, in attesa di conferma. Finche' sta
    // qui non e' partito nessun messaggio al server.
    const [nominee, setNominee] = useState(null);
    // Le due schermate della pagina: l'asta e la consultazione del listone. Si
    // alternano invece di stare una accanto all'altra perche' sul telefono
    // affiancarle non e' possibile, e perche' sono due momenti diversi —
    // rilanciare e pianificare — non due meta della stessa cosa.
    const [view, setView] = useState('asta');
    const importRef = useRef(null);

    const {
        auction,
        participantId,
        connection,
        remainingSeconds,
        messages,
        dismissMessage,
        staleProtocol,
        send,
    } = useAuctionSocket(lobbyName, session?.token);

    useEffect(() => {
        let cancelled = false;
        fetchPlayers()
            .then((payload) => {
                if (cancelled) return;
                setPlayers(payload.players);
                setCatalogue(indexPlayers(payload.players));
                setQuotazioniAvailable(payload.quotazioni_available);
            })
            .catch((error) => {
                if (!cancelled) setCatalogueError(error.message);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Uscita comune a tutti quando la lobby non esiste piu: chi ha premuto il
    // bottone e chi si e' visto chiudere il socket finiscono nello stesso
    // posto, con lo stesso avviso. La sessione va buttata, altrimenti al
    // rientro il token morto porterebbe a "Sessione non trovata".
    const leaveClosedLobby = useCallback(() => {
        clearSession(lobbyName);
        navigate('/asta-online', { replace: true, state: { notice: LOBBY_CLOSED_MESSAGE } });
    }, [lobbyName, navigate]);

    useEffect(() => {
        if (connection.closedCode === CLOSE_CODES.LOBBY_CLOSED) leaveClosedLobby();
    }, [connection.closedCode, leaveClosedLobby]);

    const handleCloseLobby = useCallback(async () => {
        setClosing({ pending: true, error: null });
        try {
            await deleteAuction(lobbyName, session.token);
            leaveClosedLobby();
        } catch (error) {
            setClosing({ pending: false, error: error.message });
        }
    }, [leaveClosedLobby, lobbyName, session?.token]);

    // Chi non e' piu disponibile: le rose gia assegnate piu il giocatore in
    // asta adesso, che nel roster di nessuno finira solo alla conferma. Senza
    // quest'ultimo il listone lo elencava fra i liberi proprio mentre il
    // timer scorreva sul suo nome.
    const excluded = useMemo(() => {
        const ids = assignedPlayerIds(auction?.teams);
        if (auction?.lot) ids.add(auction.lot.player_id);
        return ids;
    }, [auction]);

    // Se nel frattempo il turno non e' piu mio — il banditore ha annullato un
    // lotto, la fase e' cambiata, mi hanno rimosso — la conferma in sospeso non
    // vale piu: si chiude da sola, invece di lasciare un "Chiama" che
    // spedirebbe una chiamata a un'asta diversa da quella che si stava
    // guardando.
    const turnHolder = auction?.current_turn_participant_id;
    const auctionState = auction?.state;
    useEffect(() => {
        setNominee(null);
    }, [turnHolder, auctionState]);

    // "Rientra" non butta piu la sessione: il token e proprio cio che permette
    // di riprendere la propria squadra. Si cancella solo quando il server lo ha
    // dichiarato morto (sessione non valida, o partecipante rimosso), perche in
    // quel caso portarselo dietro non servirebbe a nulla.
    const rejoin = useCallback(() => {
        if (
            connection.closedCode === CLOSE_CODES.BAD_TOKEN ||
            connection.closedCode === CLOSE_CODES.REMOVED
        ) {
            clearSession(lobbyName);
        }
        navigate('/asta-online', { state: { lobbyName } });
    }, [connection.closedCode, lobbyName, navigate]);

    const handleExport = useCallback(() => {
        if (!auction) return;
        const named = {};
        auction.participants.forEach((participant) => {
            named[participant.name] = auction.teams[participant.id];
        });
        const csv = encodeTeams(named, catalogue);
        if (!csv) return;
        downloadCsv(csv, `${lobbyName}.csv`);
    }, [auction, catalogue, lobbyName]);

    const handleImport = useCallback(
        (event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (loaded) => {
                const parsed = parseTeams(String(loaded.target.result), catalogue);
                send({
                    type: 'import_teams',
                    teams: Object.fromEntries(
                        Object.entries(parsed).map(([name, team]) => [name, { roster: team.roster }]),
                    ),
                });
            };
            reader.readAsText(file);
        },
        [catalogue, send],
    );

    if (!session?.token) {
        return (
            <CenteredNotice
                title="Sessione non trovata"
                body="Non risulti collegato a questa asta da questo dispositivo."
                action={<Link className="btn btn--primary" to="/asta-online">Entra nell&apos;asta</Link>}
            />
        );
    }

    if (catalogueError) {
        return (
            <CenteredNotice
                title="Listone non disponibile"
                body={catalogueError}
                action={
                    <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
                        Riprova
                    </button>
                }
            />
        );
    }

    if (!auction) {
        // Con l'intestazione, non come testo nudo in alto a sinistra: una
        // pagina che si sta collegando deve somigliare all'applicazione, non a
        // un errore.
        return (
            <Shell title={lobbyName}>
                <ConnectionBanner
                    connection={connection}
                    staleProtocol={staleProtocol}
                    onRejoin={rejoin}
                />
                <main className="app-main">
                    <p className="u-dim">Caricamento dell&apos;asta…</p>
                </main>
            </Shell>
        );
    }

    const isAuctioneer = auction.auctioneer_id === participantId;
    const isRepair = auction.mode === MODES.REPAIR;
    const myTeam = auction.teams[participantId];
    const isMyTurn = auction.current_turn_participant_id === participantId;

    const canUndo = isAuctioneer && auction.log?.some((entry) => entry.type === 'player_assigned');
    const releasable = isRepair && auction.teams_assigned &&
        [STATES.SETUP, STATES.PHASE_BREAK].includes(auction.state);

    // Le stesse azioni vengono montate due volte: in riga nell'header sopra i
    // 48rem, dentro il menu a comparsa sotto. E' CSS a scegliere quale delle
    // due si vede. Il campo file sta fuori da entrambe perche' un id duplicato
    // nel documento non e' valido.
    const actions = (
        <>
            <button type="button" className="btn btn--ghost btn--sm" onClick={handleExport}>
                Esporta squadre
            </button>
            {isAuctioneer && isRepair && (
                <>
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => importRef.current?.click()}
                    >
                        Importa rose
                    </button>
                    {auction.imported_teams && (
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setShowAssignTeams(true)}
                        >
                            Assegna rose
                        </button>
                    )}
                </>
            )}
            {isAuctioneer && (
                <>
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setShowSettings(true)}
                        disabled={auction.state === STATES.BIDDING}
                    >
                        Impostazioni
                    </button>
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => {
                            setClosing({ pending: false, error: null });
                            setShowCloseLobby(true);
                        }}
                    >
                        Chiudi asta
                    </button>
                </>
            )}
        </>
    );

    const teamColumns = auction.participants.map((participant) => (
        <TeamColumn
            key={participant.id}
            name={participant.name}
            teamName={auction.teams[participant.id]?.imported_name}
            team={auction.teams[participant.id]}
            catalogue={catalogue}
            playersPerTeam={auction.settings.players_per_team}
            isOnline={auction.online_ids.includes(participant.id)}
            isCurrentTurn={auction.current_turn_participant_id === participant.id}
            hasProxyBid={auction.proxy_bidders?.includes(participant.id)}
            onRename={
                participant.id === participantId
                    ? (_old, name) => send({ type: 'rename', name })
                    : null
            }
            onRemoveParticipant={
                isAuctioneer && participant.id !== auction.auctioneer_id
                    ? () => send({ type: 'remove_participant', participant_id: participant.id })
                    : null
            }
            onRemovePlayer={
                isAuctioneer
                    ? (_name, entry) =>
                          send({
                              type: 'remove_player',
                              participant_id: participant.id,
                              player_id: entry.player_id,
                          })
                    : null
            }
            onReleasePlayer={
                releasable && (isAuctioneer || participant.id === participantId)
                    ? (_name, entry) =>
                          setReleaseTarget({ participantId: participant.id, entry })
                    : null
            }
        />
    ));

    return (
        <div className="app">
            <header className="app-header">
                <div className="app-brand">
                    <img src="/logos/seriea.png" alt="Serie A" />
                    <h1 className="u-truncate">{auction.lobby_name}</h1>
                    <PhaseTrack currentRole={auction.current_phase_role} />
                </div>
                <div className="app-actions">{actions}</div>
                <AppMenu>{actions}</AppMenu>
            </header>

            <input
                type="file"
                ref={importRef}
                accept=".csv,text/csv"
                className="u-visually-hidden"
                onChange={handleImport}
                aria-label="Importa rose da file CSV"
            />

            <ConnectionBanner
                connection={connection}
                staleProtocol={staleProtocol}
                onRejoin={rejoin}
            />

            <main className="app-main">
                {auction.state !== STATES.FINISHED && (
                    <div className="btn-group view-switch" role="group" aria-label="Schermata">
                        <button
                            type="button"
                            className={`btn btn--sm ${view === 'asta' ? 'btn--primary' : 'btn--ghost'}`}
                            onClick={() => setView('asta')}
                        >
                            Asta
                        </button>
                        <button
                            type="button"
                            className={`btn btn--sm ${view === 'listone' ? 'btn--primary' : 'btn--ghost'}`}
                            onClick={() => setView('listone')}
                        >
                            Listone
                        </button>
                    </div>
                )}

                {auction.state === STATES.FINISHED ? (
                    <AuctionFinishedPanel
                        participants={auction.participants}
                        teams={auction.teams}
                        playersPerTeam={auction.settings.players_per_team}
                        catalogue={catalogue}
                        onExport={handleExport}
                    />
                ) : view === 'listone' ? (
                    <>
                        <SplitPane
                            left={
                                <PlayerBrowser
                                    players={players}
                                    excludedIds={excluded}
                                    selectedId={selectedPlayer?.ID}
                                    onSelect={setSelectedPlayer}
                                />
                            }
                            right={<PlayerDisplay player={selectedPlayer} />}
                        />

                        {auction.state === STATES.BIDDING && (
                            <button
                                type="button"
                                className="btn btn--stop auction-alert"
                                onClick={() => setView('asta')}
                            >
                                Asta in corso
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <SplitPane
                            left={
                              <div className="stack">
                                {/* Sopra il pannello attivo, non dentro: cosi
                                    il banditore lo raggiunge anche mentre il
                                    timer scorre, che e' il caso che conta. */}
                                {isAuctioneer && (
                                    <CallModeSwitch
                                        callMode={auction.call_mode ?? CALL_MODES.FREE}
                                        baseHolder={
                                            auction.base_holder ?? BASE_HOLDERS.NOBODY
                                        }
                                        auctionState={auction.state}
                                        onChange={(change) =>
                                            send({ type: 'set_call_mode', ...change })
                                        }
                                    />
                                )}
                                <ActivePanel
                                    auction={auction}
                                    isAuctioneer={isAuctioneer}
                                    isMyTurn={isMyTurn}
                                    myTeam={myTeam}
                                    participantId={participantId}
                                    catalogue={catalogue}
                                    players={players}
                                    excluded={excluded}
                                    remainingSeconds={remainingSeconds}
                                    onSelectPlayer={setSelectedPlayer}
                                    onNominate={setNominee}
                                    send={send}
                                />
                              </div>
                            }
                            right={
                                <PlayerDisplay
                                    player={
                                        auction.lot
                                            ? catalogue[auction.lot.player_id]
                                            : auction.pending_pick
                                              ? catalogue[auction.pending_pick.player_id]
                                              : selectedPlayer
                                    }
                                />
                            }
                        />

                        <section className="stack" aria-label="Rose">
                            <div className="roster-grid">{teamColumns}</div>
                        </section>

                        <AuctionLog
                            log={auction.log}
                            canUndo={canUndo}
                            onUndo={() => send({ type: 'undo_last_assignment' })}
                        />
                    </>
                )}
            </main>

            {showSettings && (
                <AuctionSettingsModal
                    show={showSettings}
                    settings={auction.settings}
                    onSave={(changes) => send({ type: 'update_settings', ...changes })}
                    onClose={() => setShowSettings(false)}
                />
            )}

            {showAssignTeams && auction.imported_teams && (
                <TeamAssignment
                    participants={auction.participants}
                    importedTeams={auction.imported_teams}
                    onAssign={(assignments) => {
                        send({ type: 'assign_teams', assignments });
                        setShowAssignTeams(false);
                    }}
                    onClose={() => setShowAssignTeams(false)}
                />
            )}

            <NominatePlayerModal
                player={nominee}
                onConfirm={() => {
                    send({ type: 'nominate', player_id: nominee.ID });
                    setNominee(null);
                }}
                onClose={() => setNominee(null)}
            />

            <CloseLobbyModal
                show={showCloseLobby}
                lobbyName={auction.lobby_name}
                error={closing.error}
                pending={closing.pending}
                onConfirm={handleCloseLobby}
                onClose={() => setShowCloseLobby(false)}
            />

            <ReleasePlayerModal
                show={Boolean(releaseTarget)}
                entry={releaseTarget?.entry}
                player={catalogue[releaseTarget?.entry?.player_id]}
                quotazioniAvailable={quotazioniAvailable}
                onConfirm={() => {
                    send({
                        type: 'release_player',
                        participant_id: releaseTarget.participantId,
                        player_id: releaseTarget.entry.player_id,
                    });
                    setReleaseTarget(null);
                }}
                onClose={() => setReleaseTarget(null)}
            />

            <Toasts messages={messages} onDismiss={dismissMessage} />
        </div>
    );
}

// Il pannello sinistro cambia con lo stato dell'asta: e' sempre "la cosa che
// devi fare adesso".
function ActivePanel({
    auction,
    isAuctioneer,
    isMyTurn,
    myTeam,
    participantId,
    catalogue,
    players,
    excluded,
    remainingSeconds,
    onSelectPlayer,
    onNominate,
    send,
}) {
    const waitingFor = auction.participants.find(
        (participant) => participant.id === auction.current_turn_participant_id,
    );

    if (auction.state === STATES.BIDDING) {
        return (
            <LotCard
                lot={auction.lot}
                remainingSeconds={remainingSeconds}
                auctionTimer={auction.settings.auction_timer_x}
                participants={auction.participants}
                team={myTeam}
                playersPerTeam={auction.settings.players_per_team}
                participantId={participantId}
                catalogue={catalogue}
                hasProxyBid={auction.proxy_bidders?.includes(participantId)}
                onBid={(amount) => send({ type: 'bid', amount })}
                onProxyBid={(max) => send({ type: 'set_proxy_bid', max_amount: max })}
            />
        );
    }

    if (auction.state === STATES.AWAITING_CONFIRM) {
        return (
            <AuctionResultPanel
                lot={auction.lot}
                participants={auction.participants}
                teams={auction.teams}
                playersPerTeam={auction.settings.players_per_team}
                catalogue={catalogue}
                isAuctioneer={isAuctioneer}
                onConfirm={(winnerId, price) =>
                    send({ type: 'confirm_assignment', participant_id: winnerId, price })
                }
                onCancel={() => send({ type: 'cancel_assignment' })}
            />
        );
    }

    if (auction.state === STATES.SETUP || auction.state === STATES.PHASE_BREAK) {
        if (isAuctioneer) {
            return (
                <AuctioneerLobby
                    participants={auction.participants}
                    // L'ordine gia salvato, cosi il pannello mostra quello
                    // vero: prima elencava sempre l'ordine d'ingresso, e alla
                    // pausa fra una fase e l'altra lo riscriveva sopra quello
                    // scelto dal banditore.
                    turnOrder={auction.turn_order}
                    phaseRole={auction.current_phase_role}
                    expectedParticipants={auction.settings.num_participants}
                    onStartPhase={(ids) =>
                        send({ type: 'start_phase', participant_ids: ids })
                    }
                />
            );
        }
        return (
            <Waiting>
                In attesa che il banditore avvii la fase {auction.current_phase_role}.
            </Waiting>
        );
    }

    // Chiamata non libera: nessuno sceglie, il server ha gia estratto e il
    // timer lo fa partire il banditore.
    // Il confronto e' positivo di proposito: uno snapshot o un server vecchio
    // non ha `call_mode`, e deve continuare a valere la chiamata libera.
    if (auction.call_mode && auction.call_mode !== CALL_MODES.FREE) {
        return (
            <PendingCallPanel
                pick={auction.pending_pick}
                player={
                    auction.pending_pick
                        ? catalogue[auction.pending_pick.player_id]
                        : null
                }
                callMode={auction.call_mode}
                baseHolder={auction.base_holder}
                turnHolder={waitingFor}
                isAuctioneer={isAuctioneer}
                skippedCount={auction.skipped_player_ids?.length ?? 0}
                onStart={() => send({ type: 'start_pending_lot' })}
                onRedraw={() => send({ type: 'redraw_nomination' })}
                onRestore={() => send({ type: 'restore_skipped' })}
            />
        );
    }

    if (isMyTurn) {
        return (
            <PlayerSearch
                title={`🔍︎ Chiama un ${auction.current_phase_role}`}
                players={players}
                excludedIds={excluded}
                roleFilter={auction.current_phase_role}
                team={myTeam}
                playersPerTeam={auction.settings.players_per_team}
                clearOnSelect={false}
                // Toccare una riga apre la scheda e chiede conferma: la
                // chiamata parte solo dal modale.
                onSelect={(player) => {
                    onSelectPlayer(player);
                    onNominate(player);
                }}
            />
        );
    }

    return <Waiting>In attesa che {waitingFor?.name ?? '…'} chiami un giocatore.</Waiting>;
}

function Waiting({ children }) {
    return (
        <div className="panel">
            <div className="panel-body u-dim">{children}</div>
        </div>
    );
}

// Intestazione minima, condivisa dagli stati in cui l'asta non c'e' ancora
// (in collegamento) o non c'e' piu (sessione persa, listone irraggiungibile).
function Shell({ title, children }) {
    return (
        <div className="app">
            <header className="app-header">
                <div className="app-brand">
                    <img src="/logos/seriea.png" alt="Serie A" />
                    <h1 className="u-truncate">{title ?? 'AstaFanta'}</h1>
                </div>
            </header>
            {children}
        </div>
    );
}

function CenteredNotice({ title, body, action }) {
    return (
        <Shell>
            <main className="app-main">
                <div className="panel panel--centered">
                    <div className="panel-body stack">
                        <h2>{title}</h2>
                        <p>{body}</p>
                        <div className="panel-actions">{action}</div>
                    </div>
                </div>
            </main>
        </Shell>
    );
}
